import { useTranslation } from "react-i18next";
import { useMissionTask } from "../../../../providers/MissionTaskProvider";
import { Helmet } from "react-helmet-async";
import NavigationLayout from "../../navigation/NavigationLayout";
import { NavigationProvider } from "../../navigation/NavigationProvider";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { RouteEnums, TaskEnums } from "../../../../common/constants";
import NavigationArrivedDialog from "../common/NavigationArrivedDialog";
import GoToMyMissionsDialog from "../common/GoToMyMissionsDialog";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { FormatLocationShort } from "../../../../common/maphelpers";
import { PlaceOutlined as PlaceIcon, FlagOutlined as FlagIcon, Close as CloseIcon } from "@mui/icons-material";

export default function MissionTaskNavigationPage() {
  const { t } = useTranslation();
  const { taskState, missionTask, mission, taskId } = useMissionTask();

  const [arrived, setArrived] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [selectDialogClosed, setSelectDialogClosed] = useState(false)
  const inTransport = taskState >= TaskEnums.State.Arrived;

  const navigate = useNavigate();

  const onCancel = useCallback(() => {
    navigate(`../`, { relative: "path" });
  }, [navigate]);

  const destination = useMemo(() => {
    const waypoints = missionTask?.route?.waypoints?.filter(t => t.coordinates?.length === 2);

    if (waypoints.length !== 0) {

      if (inTransport && waypoints.length > 2) {
        return null;
      }

      return inTransport ? waypoints[waypoints.length - 1] : waypoints[0];
    } else {
      return null;
    }
  }, [missionTask, inTransport]);


  const onReady = useCallback((navCore) => {
    navCore.instructions.on("arrived", () => {
      setArrived(true);
    });
  }, []);

  const metadata = useMemo(() => ({
    navigationType: inTransport ? RouteEnums.NavigationType.InRoute : RouteEnums.NavigationType.ToRoute,
    missionId: mission?.id,
    taskId: taskId,
  }), [inTransport, taskId, mission?.id]);

  const onCloseSelectDialog = useCallback(() => {
    setSelectDialogClosed(true);
  }, [])

  return (
    <NavigationProvider destination={destination || selectedDestination} onReady={onReady} metadata={metadata} onCancel={onCancel}>

      <Helmet>
        <title>Assist - {t("navigation:title")}</title>
      </Helmet>

      <NavigationLayout backButton="../" description={inTransport ? t("task:common:navToTransport") : t("task:common:navToLocation")} />

      {arrived ? <NavigationArrivedDialog onClose={() => setArrived(false)} /> : null}

      {
        !!missionTask && inTransport && destination === null && selectedDestination === null && !selectDialogClosed ? (
          <DestinationSelectDialog onCancel={onCloseSelectDialog} onChange={setSelectedDestination} missionTask={missionTask} />
        ) : null
      }

      <GoToMyMissionsDialog />

    </NavigationProvider>
  );
}



function DestinationSelectDialog(props) {
  const { onCancel, onChange, missionTask } = props;
  const { t } = useTranslation();
  const waypoints = useMemo(() => missionTask?.route?.waypoints?.filter((t) => t.coordinates.length === 2).filter((_, i) => i > 0), [missionTask])

  return (
    <Dialog
      open
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title">
        {t("task:destinationSelectDialog:title")}
        <IconButton onClick={onCancel} color="primary" sx={{ float: "right", marginRight: -1 }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("task:destinationSelectDialog:description")}
        </DialogContentText>

        <List disablePadding>
          {waypoints.map((wp, idx) => (
            <ListItemButton key={idx} divider onClick={() => onChange(wp)} disableGutters>
              <ListItemIcon>
                {idx !== waypoints.length - 1 ? <PlaceIcon /> : <FlagIcon />}
              </ListItemIcon>
              <ListItemText
                primary={idx !== waypoints.length - 1 ? `${t("task:destinationSelectDialog:waypoint")} ${idx + 1}` : t("task:destinationSelectDialog:destination")}
                secondary={FormatLocationShort(wp)}
              />
            </ListItemButton>
          ))}
        </List>

      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="neutral" onClick={onCancel} >
          {t("common:cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}